/**
 * Admin Projects Page — list all projects with search/filter
 */

import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../../services/api';
import type { Project } from '../../../shared/types/project';
import { formatProjectName } from '../../../shared/formatProjectName';
import { ProjectImportModal } from '../../components/admin/ProjectImportModal';
import { ProjectsMapView } from '../../components/portal/ProjectsMapView';

type ViewMode = 'list' | 'map';

function MultiSelect({
  selected,
  onChange,
  options,
  placeholder,
}: {
  selected: Set<string>;
  onChange: (v: Set<string>) => void;
  options: string[];
  placeholder: string;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  const filtered = options.filter((o) => o.toLowerCase().includes(query.toLowerCase()));

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const toggle = (val: string) => {
    const next = new Set(selected);
    if (next.has(val)) next.delete(val);
    else next.add(val);
    onChange(next);
  };

  const label = selected.size === 0
    ? placeholder
    : selected.size === 1
      ? [...selected][0]
      : `${[...selected][0]} +${selected.size - 1}`;

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        onClick={() => { setOpen(!open); setQuery(''); }}
        style={{
          ...filterSelectStyle,
          color: selected.size > 0 ? '#f1f5f9' : '#64748b',
          borderColor: selected.size > 0 ? '#4F7CFF' : '#333333',
        }}
      >
        {label}
        {selected.size > 0 && (
          <span style={badgeStyle}>{selected.size}</span>
        )}
        <span style={{ marginLeft: 6, fontSize: 10 }}>{open ? '\u25B2' : '\u25BC'}</span>
      </button>
      {open && (
        <div style={dropdownStyle}>
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search..."
            style={dropdownSearchStyle}
            autoFocus
          />
          {selected.size > 0 && (
            <div
              onClick={() => onChange(new Set())}
              style={{ padding: '6px 12px', fontSize: 11, color: '#4F7CFF', cursor: 'pointer', borderBottom: '1px solid #333333' }}
            >
              Clear selection
            </div>
          )}
          <div style={dropdownListStyle}>
            {filtered.map((o) => (
              <label
                key={o}
                style={{ ...dropdownItemStyle, display: 'flex', alignItems: 'center', gap: 8 }}
              >
                <input
                  type="checkbox"
                  checked={selected.has(o)}
                  onChange={() => toggle(o)}
                  style={{ accentColor: '#4F7CFF', cursor: 'pointer', flexShrink: 0 }}
                />
                <span style={{ color: selected.has(o) ? '#f1f5f9' : '#94a3b8' }}>{o}</span>
              </label>
            ))}
            {filtered.length === 0 && (
              <div style={{ ...dropdownItemStyle, color: '#475569', fontStyle: 'italic' }}>No matches</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const filterSelectStyle: React.CSSProperties = {
  background: '#1a1a1a',
  border: '1px solid #333333',
  borderRadius: 8,
  padding: '8px 12px',
  fontSize: 13,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  whiteSpace: 'nowrap',
  width: '100%',
  boxSizing: 'border-box',
  minHeight: 40,
};

const badgeStyle: React.CSSProperties = {
  background: '#4F7CFF',
  color: '#fff',
  borderRadius: 10,
  padding: '1px 6px',
  fontSize: 10,
  fontWeight: 700,
  marginLeft: 6,
};

const dropdownStyle: React.CSSProperties = {
  position: 'absolute',
  top: '100%',
  left: 0,
  marginTop: 4,
  background: '#1a1a1a',
  border: '1px solid #333333',
  borderRadius: 8,
  width: 220,
  zIndex: 100,
  boxShadow: '0 12px 32px rgba(0,0,0,0.5)',
  overflow: 'hidden',
};

const dropdownSearchStyle: React.CSSProperties = {
  width: '100%',
  background: '#0d0d0d',
  border: 'none',
  borderBottom: '1px solid #333333',
  padding: '10px 12px',
  color: '#f1f5f9',
  fontSize: 13,
  outline: 'none',
  boxSizing: 'border-box',
};

const dropdownListStyle: React.CSSProperties = {
  maxHeight: 200,
  overflow: 'auto',
};

const dropdownItemStyle: React.CSSProperties = {
  padding: '8px 12px',
  fontSize: 13,
  cursor: 'pointer',
};

export function ProjectsPage() {
  const [projects, setProjects] = useState<Project[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importNotice, setImportNotice] = useState('');
  const [view, setView] = useState<ViewMode>(() => (localStorage.getItem('admin_projects_view') as ViewMode) || 'list');
  const switchView = (v: ViewMode) => { setView(v); localStorage.setItem('admin_projects_view', v); };
  const [filterClient, setFilterClient] = useState<Set<string>>(new Set());
  const [filterEndClient, setFilterEndClient] = useState<Set<string>>(new Set());
  const [filterCity, setFilterCity] = useState<Set<string>>(new Set());
  const [filterState, setFilterState] = useState<Set<string>>(new Set());
  const [filterCategory, setFilterCategory] = useState<Set<string>>(new Set());
  const [showFilters, setShowFilters] = useState(false);
  const navigate = useNavigate();

  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  const load = () => {
    api.project.getAll()
      .then(setProjects)
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  // Build unique option lists for filter dropdowns
  const clientOptions = [...new Set(projects.map((p: any) => p.clientName).filter(Boolean))].sort() as string[];
  const endClientOptions = [...new Set(projects.map((p) => p.endClient).filter(Boolean))].sort() as string[];
  const cityOptions = [...new Set(projects.map((p) => p.city).filter(Boolean))].sort() as string[];
  const stateOptions = [...new Set(projects.map((p) => p.state).filter(Boolean))].sort() as string[];
  const categoryOptions = [...new Set(projects.map((p) => p.category).filter(Boolean))].sort() as string[];

  const filtered = projects.filter((p) => {
    const q = search.toLowerCase();
    const cn = (p as any).clientName as string | undefined;
    const matchesSearch = !q || (
      p.name.toLowerCase().includes(q) ||
      (cn?.toLowerCase().includes(q)) ||
      (p.endClient?.toLowerCase().includes(q)) ||
      (p.city?.toLowerCase().includes(q)) ||
      (p.category?.toLowerCase().includes(q))
    );
    const matchesClient = filterClient.size === 0 || (cn != null && filterClient.has(cn));
    const matchesEndClient = filterEndClient.size === 0 || (p.endClient != null && filterEndClient.has(p.endClient));
    const matchesCity = filterCity.size === 0 || (p.city != null && filterCity.has(p.city));
    const matchesState = filterState.size === 0 || (p.state != null && filterState.has(p.state));
    const matchesCategory = filterCategory.size === 0 || (p.category != null && filterCategory.has(p.category));
    return matchesSearch && matchesClient && matchesEndClient && matchesCity && matchesState && matchesCategory;
  });

  const activeFilterCount = filterClient.size + filterEndClient.size + filterCity.size + filterState.size + filterCategory.size;

  const clearFilters = () => {
    setFilterClient(new Set());
    setFilterEndClient(new Set());
    setFilterCity(new Set());
    setFilterState(new Set());
    setFilterCategory(new Set());
  };

  const locateProjects = async () => {
    try {
      const r = await api.admin.geocodeProjects(false);
      setImportNotice(`Located ${r.located} of ${r.attempted} project${r.attempted === 1 ? '' : 's'}${r.failed ? `, ${r.failed} could not be resolved` : ''}${r.noAddress ? `, ${r.noAddress} have no address` : ''}.`);
    } catch (err: any) {
      setImportNotice(err.message || 'Failed to locate projects');
    }
    load();
  };

  const handleNewProject = async () => {
    try {
      const project = await api.project.create({ name: 'New Project' });
      navigate(`/admin/projects/${project.id}`);
    } catch (err) {
      console.error('Failed to create project:', err);
    }
  };

  const toggleSelect = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selected.size === filtered.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(filtered.map((p) => p.id)));
    }
  };

  const handleDeleteComplete = () => {
    setShowDeleteModal(false);
    setSelected(new Set());
    load();
  };

  if (loading) return <div style={{ color: '#94a3b8' }}>Loading projects...</div>;

  const viewToggle = (
    <div style={styles.viewToggle} role="group" aria-label="View mode">
      <button onClick={() => switchView('list')} style={{ ...styles.viewBtn, ...(view === 'list' ? styles.viewBtnActive : {}) }} title="List view">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>
        {!isMobile && 'List'}
      </button>
      <button onClick={() => switchView('map')} style={{ ...styles.viewBtn, ...(view === 'map' ? styles.viewBtnActive : {}) }} title="Map view">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
        {!isMobile && 'Map'}
      </button>
    </div>
  );

  // ───── Map view: edge-to-edge, controls float over the map, filters live in the panel ─────
  if (view === 'map') {
    return (
      <ProjectsMapView
        layout="fullscreen"
        mode="admin"
        projects={filtered}
        onLocate={locateProjects}
        searchValue={search}
        onSearchChange={setSearch}
        overlayTopLeft={(
          <>
            {viewToggle}
            <button onClick={handleNewProject} style={{ ...styles.floatBtn, ...styles.floatBtnPrimary }}>+ New Project</button>
            {importNotice && (
              <span style={styles.floatNotice}>{importNotice}<button onClick={() => setImportNotice('')} style={styles.noticeClose}>&times;</button></span>
            )}
          </>
        )}
        panelExtras={(
          <>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
              <MultiSelect selected={filterClient} onChange={setFilterClient} options={clientOptions} placeholder="All Clients" />
              <MultiSelect selected={filterCategory} onChange={setFilterCategory} options={categoryOptions} placeholder="All Categories" />
              <MultiSelect selected={filterState} onChange={setFilterState} options={stateOptions} placeholder="All States" />
              <MultiSelect selected={filterCity} onChange={setFilterCity} options={cityOptions} placeholder="All Cities" />
            </div>
            {activeFilterCount > 0 && (
              <button onClick={clearFilters} style={{ ...styles.clearFilterBtn, alignSelf: 'flex-start', padding: '2px 4px' }}>Clear filters ({activeFilterCount})</button>
            )}
          </>
        )}
      />
    );
  }

  return (
    <div>
      <div style={{
        ...styles.header,
        ...(isMobile ? { flexDirection: 'column' as const, alignItems: 'stretch', gap: 12 } : {}),
      }}>
        <h1 style={{ ...styles.heading, ...(isMobile ? { fontSize: 20 } : {}) }}>Projects</h1>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          {viewToggle}
          {selected.size > 0 && (
            <button onClick={() => setShowDeleteModal(true)} style={{
              ...styles.deleteBtn,
              ...(isMobile ? { minHeight: 44, flex: 1 } : {}),
            }}>
              Delete ({selected.size})
            </button>
          )}
          <button onClick={() => setShowImportModal(true)} style={{
            ...styles.importBtn,
            ...(isMobile ? { minHeight: 44, flex: 1 } : {}),
          }}>Import CSV</button>
          <button onClick={handleNewProject} style={{
            ...styles.newBtn,
            ...(isMobile ? { minHeight: 44, flex: 1 } : {}),
          }}>+ New Project</button>
        </div>
      </div>

      {importNotice && (
        <div style={styles.notice}>
          <span>{importNotice}</span>
          <button onClick={() => setImportNotice('')} style={styles.noticeClose}>&times;</button>
        </div>
      )}

      <input
        type="text"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search projects..."
        style={{ ...styles.search, ...(isMobile ? { fontSize: 16 } : {}) }}
      />

      {/* Filter bar — collapsible on mobile */}
      {isMobile ? (
        <div style={{ marginBottom: 16 }}>
          <button
            onClick={() => setShowFilters(!showFilters)}
            style={{
              background: activeFilterCount > 0 ? 'rgba(79,124,255,0.15)' : '#1a1a1a',
              border: `1px solid ${activeFilterCount > 0 ? '#4F7CFF' : '#333333'}`,
              borderRadius: 8,
              padding: '10px 16px',
              color: activeFilterCount > 0 ? '#4F7CFF' : '#94a3b8',
              fontSize: 14,
              cursor: 'pointer',
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              minHeight: 44,
            }}
          >
            <span>Filters {activeFilterCount > 0 ? `(${activeFilterCount} active)` : ''}</span>
            <span style={{ fontSize: 10 }}>{showFilters ? '\u25B2' : '\u25BC'}</span>
          </button>
          {showFilters && (
            <div style={{
              background: '#1a1a1a',
              border: '1px solid #333333',
              borderRadius: 8,
              padding: 12,
              marginTop: 8,
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
            }}>
              <MultiSelect selected={filterClient} onChange={setFilterClient} options={clientOptions} placeholder="All Clients" />
              <MultiSelect selected={filterEndClient} onChange={setFilterEndClient} options={endClientOptions} placeholder="All End Clients" />
              <MultiSelect selected={filterCity} onChange={setFilterCity} options={cityOptions} placeholder="All Cities" />
              <MultiSelect selected={filterState} onChange={setFilterState} options={stateOptions} placeholder="All States" />
              <MultiSelect selected={filterCategory} onChange={setFilterCategory} options={categoryOptions} placeholder="All Categories" />
              {activeFilterCount > 0 && (
                <button onClick={clearFilters} style={{
                  ...styles.clearFilterBtn,
                  minHeight: 44,
                  textAlign: 'center',
                  fontSize: 14,
                }}>
                  Clear all filters ({activeFilterCount})
                </button>
              )}
            </div>
          )}
          <div style={{ color: '#64748b', fontSize: 12, marginTop: 8 }}>
            {filtered.length} of {projects.length} projects
          </div>
        </div>
      ) : (
        <div style={styles.filterBar}>
          <MultiSelect selected={filterClient} onChange={setFilterClient} options={clientOptions} placeholder="All Clients" />
          <MultiSelect selected={filterEndClient} onChange={setFilterEndClient} options={endClientOptions} placeholder="All End Clients" />
          <MultiSelect selected={filterCity} onChange={setFilterCity} options={cityOptions} placeholder="All Cities" />
          <MultiSelect selected={filterState} onChange={setFilterState} options={stateOptions} placeholder="All States" />
          <MultiSelect selected={filterCategory} onChange={setFilterCategory} options={categoryOptions} placeholder="All Categories" />
          {activeFilterCount > 0 && (
            <button onClick={clearFilters} style={styles.clearFilterBtn}>
              Clear filters ({activeFilterCount})
            </button>
          )}
          <span style={{ marginLeft: 'auto', color: '#64748b', fontSize: 12 }}>
            {filtered.length} of {projects.length} projects
          </span>
        </div>
      )}

      {/* Project list */}
      <div style={styles.table}>
        {/* Table header — desktop only */}
        {!isMobile && (
          <div style={styles.tableHeader}>
            <span style={{ width: 36 }}>
              <input
                type="checkbox"
                checked={filtered.length > 0 && selected.size === filtered.length}
                onChange={toggleSelectAll}
                style={{ accentColor: '#4F7CFF', cursor: 'pointer' }}
              />
            </span>
            <span style={{ flex: 1, minWidth: 220 }}>Name</span>
            <span style={styles.colClient}>Client</span>
            <span style={styles.colEndClient}>End Client</span>
            <span style={styles.colLocation}>Location</span>
            <span style={styles.colCategory}>Category</span>
            <span style={styles.colUpdated}>Updated</span>
          </div>
        )}
        {filtered.map((p) => (
          isMobile ? (
            // ───── Mobile project card ─────
            <div key={p.id} style={{
              padding: 16,
              borderBottom: '1px solid #0d0d0d',
            }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 10 }}>
                <input
                  type="checkbox"
                  checked={selected.has(p.id)}
                  onChange={() => toggleSelect(p.id)}
                  style={{ accentColor: '#4F7CFF', cursor: 'pointer', marginTop: 3, flexShrink: 0 }}
                />
                <Link
                  to={`/admin/projects/${p.id}`}
                  style={{ flex: 1, fontWeight: 600, color: '#f1f5f9', textDecoration: 'none', fontSize: 15, lineHeight: 1.3 }}
                >
                  {formatProjectName(p)}
                </Link>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 16px', paddingLeft: 28 }}>
                {(p as any).clientName && (
                  <span style={{ color: '#4F7CFF', fontSize: 12, fontWeight: 500 }}>
                    {(p as any).clientName}
                  </span>
                )}
                {p.endClient && (
                  <span style={{ color: '#94a3b8', fontSize: 12 }}>
                    {p.endClient}
                  </span>
                )}
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', paddingLeft: 28, marginTop: 6 }}>
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                  {(p.city || p.state) && (
                    <span style={{ color: '#64748b', fontSize: 12 }}>
                      {p.city}{p.state ? `, ${p.state}` : ''}
                    </span>
                  )}
                  {p.category && (
                    <span style={{
                      background: '#262626',
                      color: '#94a3b8',
                      padding: '1px 8px',
                      borderRadius: 10,
                      fontSize: 11,
                      fontWeight: 500,
                    }}>
                      {p.category}
                    </span>
                  )}
                </div>
                <span style={{ color: '#475569', fontSize: 11, flexShrink: 0 }}>
                  {new Date(p.updatedAt).toLocaleDateString()}
                </span>
              </div>
            </div>
          ) : (
            // ───── Desktop row ─────
            <div key={p.id} style={styles.row}>
              <span style={{ width: 36 }} onClick={(e) => e.stopPropagation()}>
                <input
                  type="checkbox"
                  checked={selected.has(p.id)}
                  onChange={() => toggleSelect(p.id)}
                  style={{ accentColor: '#4F7CFF', cursor: 'pointer' }}
                />
              </span>
              <Link to={`/admin/projects/${p.id}`} style={{ flex: 1, minWidth: 220, fontWeight: 500, color: '#f1f5f9', textDecoration: 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {formatProjectName(p)}
              </Link>
              <span style={{ ...styles.colClient, ...styles.cell }}>{(p as any).clientName || '—'}</span>
              <span style={{ ...styles.colEndClient, ...styles.cell }}>{p.endClient || '—'}</span>
              <span style={{ ...styles.colLocation, ...styles.cell }}>
                {[p.city, p.state].filter(Boolean).join(', ') || '—'}
              </span>
              <span style={{ ...styles.colCategory, ...styles.cell, textTransform: 'capitalize' }}>{p.category || '—'}</span>
              <span style={{ ...styles.colUpdated, ...styles.cell }}>
                {new Date(p.updatedAt).toLocaleDateString()}
              </span>
            </div>
          )
        ))}
        {filtered.length === 0 && (
          <div style={{ padding: 20, color: '#64748b', fontSize: 14, textAlign: 'center' }}>
            {search ? 'No matching projects' : 'No projects yet'}
          </div>
        )}
      </div>

      {showDeleteModal && (
        <DeleteConfirmModal
          projects={projects.filter((p) => selected.has(p.id))}
          onClose={() => setShowDeleteModal(false)}
          onDeleted={handleDeleteComplete}
        />
      )}

      {showImportModal && (
        <ProjectImportModal
          onClose={() => setShowImportModal(false)}
          onImported={(created) => {
            setImportNotice(`Imported ${created} project${created === 1 ? '' : 's'}.`);
            load();
          }}
        />
      )}
    </div>
  );
}

function DeleteConfirmModal({
  projects: toDelete,
  onClose,
  onDeleted,
}: {
  projects: Project[];
  onClose: () => void;
  onDeleted: () => void;
}) {
  const [confirmText, setConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');

  const expectedText = toDelete.length === 1 ? toDelete[0].name : `delete ${toDelete.length} projects`;
  const isConfirmed = confirmText === expectedText;

  const handleDelete = async () => {
    if (!isConfirmed) return;
    setDeleting(true);
    setError('');
    try {
      await Promise.all(toDelete.map((p) => api.project.delete(p.id)));
      onDeleted();
    } catch (err: any) {
      setError(err.message || 'Failed to delete');
      setDeleting(false);
    }
  };

  return (
    <div style={modalStyles.overlay} onClick={onClose}>
      <div style={modalStyles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={modalStyles.header}>
          <h2 style={modalStyles.title}>Delete {toDelete.length === 1 ? 'Project' : `${toDelete.length} Projects`}</h2>
          <button onClick={onClose} style={modalStyles.closeBtn}>&times;</button>
        </div>

        <div style={modalStyles.body}>
          <p style={{ color: '#fca5a5', fontSize: 14, margin: '0 0 16px', lineHeight: 1.5 }}>
            This action is permanent and cannot be undone. All associated files, assets, and share links will be deleted.
          </p>

          <div style={{ marginBottom: 16 }}>
            <div style={{ color: '#94a3b8', fontSize: 12, fontWeight: 600, marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              {toDelete.length === 1 ? 'Project to delete:' : 'Projects to delete:'}
            </div>
            <div style={modalStyles.projectList}>
              {toDelete.map((p) => (
                <div key={p.id} style={modalStyles.projectItem}>
                  <span style={{ color: '#f1f5f9', fontSize: 13 }}>{formatProjectName(p)}</span>
                  <span style={{ color: '#475569', fontSize: 11 }}>
                    {(p as any).clientName || 'No client'}{p.city ? ` — ${p.city}` : ''}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={{ color: '#94a3b8', fontSize: 12, fontWeight: 500, display: 'block', marginBottom: 6 }}>
              Type <strong style={{ color: '#f1f5f9' }}>{expectedText}</strong> to confirm
            </label>
            <input
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              style={modalStyles.input}
              placeholder={expectedText}
              autoFocus
            />
          </div>

          {error && <div style={modalStyles.error}>{error}</div>}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, flexDirection: window.innerWidth < 768 ? 'column-reverse' as const : 'row' as const }}>
            <button onClick={onClose} style={{ ...modalStyles.cancelBtn, minHeight: 44 }}>Cancel</button>
            <button
              onClick={handleDelete}
              style={{ ...modalStyles.confirmBtn, opacity: isConfirmed ? 1 : 0.4, minHeight: 44 }}
              disabled={!isConfirmed || deleting}
            >
              {deleting ? 'Deleting...' : `Delete ${toDelete.length === 1 ? 'Project' : `${toDelete.length} Projects`}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 20,
  },
  heading: {
    fontSize: 24,
    fontWeight: 700,
    color: '#f1f5f9',
    margin: 0,
  },
  newBtn: {
    background: '#4F7CFF',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    padding: '10px 20px',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
  },
  deleteBtn: {
    background: '#7f1d1d',
    color: '#fca5a5',
    border: 'none',
    borderRadius: 8,
    padding: '10px 20px',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
  },
  viewToggle: {
    display: 'flex',
    background: 'rgba(20,20,22,0.86)',
    border: '1px solid rgba(255,255,255,0.09)',
    borderRadius: 10,
    padding: 3,
    gap: 2,
    backdropFilter: 'blur(16px)',
  },
  floatBtn: {
    background: 'rgba(20,20,22,0.86)',
    border: '1px solid rgba(255,255,255,0.09)',
    color: '#f5f5f7',
    borderRadius: 10,
    padding: '8px 14px',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    backdropFilter: 'blur(16px)',
    boxShadow: '0 6px 20px rgba(0,0,0,0.4)',
  },
  floatBtnPrimary: {
    background: '#4F7CFF',
    border: '1px solid rgba(79,124,255,0.6)',
    color: '#fff',
  },
  floatNotice: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    background: 'rgba(0,204,136,0.14)',
    border: '1px solid rgba(0,204,136,0.4)',
    color: '#00cc88',
    borderRadius: 10,
    padding: '6px 12px',
    fontSize: 12,
    backdropFilter: 'blur(16px)',
  },
  viewBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    background: 'none',
    border: 'none',
    color: '#94a3b8',
    borderRadius: 6,
    padding: '7px 12px',
    fontSize: 13,
    fontWeight: 500,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  viewBtnActive: {
    background: '#333333',
    color: '#f1f5f9',
  },
  importBtn: {
    background: '#1e293b',
    color: '#94a3b8',
    border: '1px solid #334155',
    borderRadius: 8,
    padding: '10px 20px',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
  },
  notice: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    background: 'rgba(0,204,136,0.1)',
    border: '1px solid rgba(0,204,136,0.4)',
    color: '#00cc88',
    borderRadius: 8,
    padding: '10px 14px',
    fontSize: 13,
    marginBottom: 16,
  },
  noticeClose: {
    background: 'none',
    border: 'none',
    color: '#00cc88',
    fontSize: 18,
    cursor: 'pointer',
    lineHeight: 1,
  },
  filterBar: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    marginBottom: 16,
    flexWrap: 'wrap' as const,
  },
  clearFilterBtn: {
    background: 'none',
    border: 'none',
    color: '#4F7CFF',
    fontSize: 12,
    cursor: 'pointer',
    padding: '4px 8px',
  },
  search: {
    width: '100%',
    background: '#1a1a1a',
    border: '1px solid #333333',
    borderRadius: 8,
    padding: '10px 16px',
    color: '#f1f5f9',
    fontSize: 14,
    marginBottom: 16,
    outline: 'none',
    boxSizing: 'border-box' as const,
  },
  table: {
    background: '#1a1a1a',
    border: '1px solid #333333',
    borderRadius: 12,
    overflow: 'hidden',
  },
  tableHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 24,
    padding: '10px 20px',
    borderBottom: '1px solid #333333',
    color: '#64748b',
    fontSize: 12,
    fontWeight: 600,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: 24,
    padding: '13px 20px',
    borderBottom: '1px solid #1a1a1a',
    color: 'inherit',
    fontSize: 14,
  },
  // Fixed-width columns (desktop). Widths include their own breathing room; the flex gap adds more.
  colClient: { width: 170, flexShrink: 0 },
  colEndClient: { width: 150, flexShrink: 0 },
  colLocation: { width: 180, flexShrink: 0 },
  colCategory: { width: 120, flexShrink: 0 },
  colUpdated: { width: 96, flexShrink: 0, textAlign: 'right' as const },
  cell: { color: '#8e8e93', fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const },
};

const modalStyles: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
  },
  modal: {
    background: '#1a1a1a', border: '1px solid #333333', borderRadius: 16,
    width: 520, maxWidth: '90vw', maxHeight: '90vh', overflow: 'auto',
    boxShadow: '0 25px 60px rgba(0,0,0,0.5)',
  },
  header: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '20px 24px', borderBottom: '1px solid #333333',
  },
  title: { fontSize: 18, fontWeight: 600, color: '#fca5a5', margin: 0 },
  closeBtn: {
    background: 'none', border: 'none', color: '#64748b', fontSize: 24,
    cursor: 'pointer', padding: '0 4px', lineHeight: 1,
  },
  body: { padding: 24 },
  projectList: {
    background: '#0d0d0d', border: '1px solid #333333', borderRadius: 8,
    maxHeight: 180, overflow: 'auto',
  },
  projectItem: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '8px 12px', borderBottom: '1px solid #1a1a1a',
  },
  input: {
    width: '100%', background: '#0d0d0d', border: '1px solid #333333', borderRadius: 8,
    padding: '10px 14px', color: '#f1f5f9', fontSize: 14, outline: 'none',
    boxSizing: 'border-box' as const,
  },
  error: {
    background: '#7f1d1d', color: '#fca5a5', padding: '10px 14px', borderRadius: 8,
    fontSize: 13, marginBottom: 12,
  },
  cancelBtn: {
    background: 'none', border: '1px solid #475569', borderRadius: 8,
    color: '#94a3b8', padding: '10px 20px', fontSize: 14, cursor: 'pointer',
  },
  confirmBtn: {
    background: '#dc2626', color: '#fff', border: 'none', borderRadius: 8,
    padding: '10px 24px', fontSize: 14, fontWeight: 600, cursor: 'pointer',
  },
};
